import {
  CreditNoteDetailSchema,
  SupplierRefundRowSchema,
  SupplierChangeReasonSchema,
  CreditNoteListSchema,
  CreditNoteRowSchema,
  GstOutputRegisterSchema,
  TcsRegisterSchema,
  InvoiceDetailSchema,
  InvoiceEditDataSchema,
  InvoiceFormOptionsSchema,
  InvoiceGenerateResponseSchema,
  InvoiceLedgerResponseSchema,
  InvoiceListResponseSchema,
  InvoicePreviewResponseSchema,
  BankAccountSchema,
  RecordReceiptResponseSchema,
  ConvertToTaxResultSchema,
  BulkConvertResultSchema,
  CancelInvoiceResultSchema,
  RecordSupplierPaymentResponseSchema,
  SupplierLedgerSchema,
  SupplierLineSchema,
  SupplierOutstandingReportSchema,
  SupplierPaymentReportSchema,
  SupplierPaymentStatusReportSchema,
  AgentReportCardResponseSchema,
  BankReconcilePreviewResponseSchema,
  BankReconcileCommitResultSchema,
  BankReconcileRunListSchema,
  BankReconcileRunDetailSchema,
  SalesRegisterResponseSchema,
  ClientOutstandingResponseSchema,
  SupplierPaidOutstandingResponseSchema,
  BalanceDueReportResponseSchema,
  BankStatementResponseSchema,
  OperationPendingResponseSchema,
  DepositBookingResponseSchema,
  SupplierPaymentScheduleSchema,
  type CreateCreditNoteRequest,
  type MarkCreditNoteUsedRequest,
  type CreateFullInvoiceRequest,
  type CreateInvoiceRequest,
  type CreditNoteDetail,
  type CreditNoteList,
  type CreditNoteListQuery,
  type CreditNoteRow,
  type GstOutputRegister,
  type GstOutputRegisterQuery,
  type TcsRegister,
  type TcsRegisterQuery,
  type InvoiceDetail,
  type InvoiceEditData,
  type InvoiceFormOptions,
  type InvoiceGenerateRequest,
  type InvoiceGenerateResponse,
  type InvoiceLedgerResponse,
  type InvoiceListQuery,
  type InvoiceListResponse,
  type InvoicePreviewRequest,
  type InvoicePreviewResponse,
  type RecordReceiptRequest,
  type ConvertToTaxResult,
  type BulkConvertResult,
  type CancelInvoiceResult,
  type InvoiceRow,
  type BankAccount,
  type CreateBankAccount,
  type UpdateBankAccount,
  type RecordReceiptResponse,
  type RecordSupplierPaymentRequest,
  type RecordSupplierPaymentResponse,
  type SaveSupplierPaymentPlanRequest,
  type SupplierLedger,
  type SupplierLine,
  type SupplierOutstandingReport,
  type SupplierPaymentReport,
  type SupplierPaymentReportQuery,
  type SupplierPaymentStatusQuery,
  type SupplierPaymentStatusReport,
  type AgentReportCardQuery,
  type AgentReportCardResponse,
  type BankReconcileUploadRequest,
  type BankReconcilePreviewResponse,
  type BankReconcileCommitRequest,
  type BankReconcileCommitResult,
  type BankReconcileRunList,
  type BankReconcileRunDetail,
  type SalesRegisterQuery,
  type SalesRegisterResponse,
  type ClientOutstandingQuery,
  type ClientOutstandingResponse,
  type SupplierPaidOutstandingQuery,
  type SupplierPaidOutstandingResponse,
  type BalanceDueReportQuery,
  type BalanceDueReportResponse,
  type BankStatementQuery,
  type BankStatementResponse,
  type OperationPendingQuery,
  type OperationPendingResponse,
  type DepositBookingQuery,
  type DepositBookingResponse,
  type SupplierPaymentSchedule,
  type SupplierPaymentScheduleQuery,
  type SupplierRefundRow,
  type SupplierRefundListQuery,
  type ReceiveSupplierRefundRequest,
} from '@utazzo/shared';
import { z } from 'zod';

import { apiClient } from '@/lib/api-client';

/**
 * Invoicing API client (admin + finance). Every response is validated with the
 * shared Zod schema so a server change can't sneak past the UI types. Money is
 * BigInt paise serialised as decimal strings; the display layer divides by 100.
 */

export const listInvoices = async (query: InvoiceListQuery): Promise<InvoiceListResponse> => {
  const { data } = await apiClient.get<unknown>('/invoices', { params: query });
  return InvoiceListResponseSchema.parse(data);
};

export const getInvoice = async (id: string): Promise<InvoiceDetail> => {
  const { data } = await apiClient.get<unknown>(`/invoices/${id}`);
  return InvoiceDetailSchema.parse(data);
};

/**
 * Load an invoice's editable state, SHAPED to hydrate the full create-invoice
 * form in edit mode (a superset of the create payload plus display labels for
 * the customer/airline pickers). Only a PROFORMA is actually editable; the page
 * redirects to the detail view otherwise.
 */
export const getInvoiceEditData = async (id: string): Promise<InvoiceEditData> => {
  const { data } = await apiClient.get<unknown>(`/invoices/${id}/edit-data`);
  return InvoiceEditDataSchema.parse(data);
};

/**
 * Edit an existing PROFORMA invoice (full form). Sends the SAME
 * `CreateFullInvoiceRequest` payload as create; the server recomputes the whole
 * invoice + rebuilds its children. Returns the invoice `{ id, invoiceNumber }`.
 */
export const updateInvoice = async (
  id: string,
  body: CreateFullInvoiceRequest,
): Promise<InvoiceGenerateResponse> => {
  const { data } = await apiClient.patch<unknown>(`/invoices/${id}`, body);
  return InvoiceGenerateResponseSchema.parse(data);
};

/**
 * Mint a proforma invoice from a booking. The body accepts the booking uuid
 * (`bookingId`) or the human-facing `bookingNumber` (e.g. `BK-2026-...`).
 * 409 when the booking already has an invoice.
 */
export const generateInvoice = async (
  body: InvoiceGenerateRequest,
): Promise<InvoiceGenerateResponse> => {
  const { data } = await apiClient.post<unknown>('/invoices/generate', body);
  return InvoiceGenerateResponseSchema.parse(data);
};

/**
 * Create an invoice DIRECTLY from the form (no booking) - the legacy
 * `createinvoicewithgst` flow. Accounts pick a customer, choose the GST slab
 * explicitly, enter the sale amount and supplier/passenger lines. Returns the
 * new invoice `{ id, invoiceNumber }` (same shape as generate).
 */
export const createInvoice = async (
  body: CreateInvoiceRequest,
): Promise<InvoiceGenerateResponse> => {
  const { data } = await apiClient.post<unknown>('/invoices', body);
  return InvoiceGenerateResponseSchema.parse(data);
};

/**
 * Static config the full create-invoice form needs (product lists by
 * domestic/international, GST slabs, payment modes, pax types, titles and fare
 * types). Masters (airlines/suppliers/customers/branches/agents/currencies/
 * countries) are fetched from their own endpoints.
 */
export const getInvoiceFormOptions = async (): Promise<InvoiceFormOptions> => {
  const { data } = await apiClient.get<unknown>('/invoices/form-options');
  return InvoiceFormOptionsSchema.parse(data);
};

/** Distinct travel-agent names for the invoice-list agent filter. */
export const listInvoiceAgents = async (): Promise<string[]> => {
  const { data } = await apiClient.get<unknown>('/invoices/agents');
  return z.array(z.string()).parse(data);
};

/**
 * Live GST preview - runs the full pricing engine with NO DB write so the
 * create-invoice form can show CGST/SGST/IGST + gross in real time as amounts
 * and the slab change.
 */
export const previewInvoice = async (
  body: InvoicePreviewRequest,
): Promise<InvoicePreviewResponse> => {
  const { data } = await apiClient.post<unknown>('/invoices/preview', body);
  return InvoicePreviewResponseSchema.parse(data);
};

/**
 * Create an invoice from the full legacy `createinvoicewithgst` form (11
 * sections) - product/GST, customer, booking/trip context, sectors,
 * passengers, pricing breakdown, deposits and supplier detail. Returns the new
 * invoice `{ id, invoiceNumber }`.
 */
export const createFullInvoice = async (
  body: CreateFullInvoiceRequest,
): Promise<InvoiceGenerateResponse> => {
  const { data } = await apiClient.post<unknown>('/invoices/create-full', body);
  return InvoiceGenerateResponseSchema.parse(data);
};

/**
 * Payments ledger + activity log for an invoice: the receipt rows, the audit
 * events (created/updated/payment/tax-issued/cancelled) and the running
 * paid/due summary. Read-only; drives the admin panel on the detail page.
 */
export const getInvoiceLedger = async (id: string): Promise<InvoiceLedgerResponse> => {
  const { data } = await apiClient.get<unknown>(`/invoices/${id}/ledger`);
  return InvoiceLedgerResponseSchema.parse(data);
};

/**
 * Record a payment receipt against an invoice. Full payment no longer converts
 * the invoice - issuing the tax invoice is a separate manual step (single or
 * bulk). The response carries the running paid/due totals + current status.
 */
export const recordInvoiceReceipt = async (
  id: string,
  body: RecordReceiptRequest,
): Promise<RecordReceiptResponse> => {
  const { data } = await apiClient.post<unknown>(`/invoices/${id}/receipts`, body);
  return RecordReceiptResponseSchema.parse(data);
};

// ----- Manual proforma -> tax-invoice conversion -----

export const convertToTaxInvoice = async (
  id: string,
  issueDate?: string,
): Promise<ConvertToTaxResult> => {
  const { data } = await apiClient.post<unknown>(`/invoices/${id}/convert-to-tax`, { issueDate });
  return ConvertToTaxResultSchema.parse(data);
};

export const convertManyToTaxInvoice = async (
  invoiceIds: string[],
  issueDate?: string,
): Promise<BulkConvertResult> => {
  const { data } = await apiClient.post<unknown>('/invoices/convert-to-tax', {
    invoiceIds,
    issueDate,
  });
  return BulkConvertResultSchema.parse(data);
};

export const cancelInvoice = async (id: string, reason?: string): Promise<CancelInvoiceResult> => {
  const { data } = await apiClient.post<unknown>(
    `/invoices/${id}/cancel`,
    reason ? { reason } : {},
  );
  return CancelInvoiceResultSchema.parse(data);
};

/**
 * Fetch every invoice matching the current filters (all pages) for an Excel
 * export. The list endpoint is paginated, so this walks pages until exhausted.
 */
export const fetchAllInvoicesForExport = async (
  query: Omit<InvoiceListQuery, 'page' | 'pageSize'>,
): Promise<InvoiceRow[]> => {
  const pageSize = 200;
  const rows: InvoiceRow[] = [];
  let page = 1;
  for (;;) {
    const res = await listInvoices({ ...query, page, pageSize } as InvoiceListQuery);
    rows.push(...res.data);
    if (page >= res.pagination.totalPages || res.data.length === 0) break;
    page += 1;
  }
  return rows;
};

// ----- v2: credit notes -----

export const issueCreditNote = async (
  invoiceId: string,
  body: CreateCreditNoteRequest,
): Promise<CreditNoteDetail> => {
  const { data } = await apiClient.post<unknown>(`/invoices/${invoiceId}/credit-notes`, body);
  return CreditNoteDetailSchema.parse(data);
};

export const listCreditNotes = async (invoiceId: string): Promise<CreditNoteRow[]> => {
  const { data } = await apiClient.get<unknown>(`/invoices/${invoiceId}/credit-notes`);
  return CreditNoteRowSchema.array().parse(data);
};

export const getCreditNote = async (id: string): Promise<CreditNoteDetail> => {
  const { data } = await apiClient.get<unknown>(`/credit-notes/${id}`);
  return CreditNoteDetailSchema.parse(data);
};

/**
 * Global credit-note listing (finance report, legacy `creditNoteList`) - every
 * credit note across invoices for a period, with the customer/invoice each was
 * raised against and the sum of the ISSUED totals in the range.
 */
export const getCreditNotesList = async (query: CreditNoteListQuery): Promise<CreditNoteList> => {
  const { data } = await apiClient.get<unknown>('/credit-notes', { params: query });
  return CreditNoteListSchema.parse(data);
};

export const cancelCreditNote = async (id: string): Promise<CreditNoteDetail> => {
  const { data } = await apiClient.post<unknown>(`/credit-notes/${id}/cancel`, {});
  return CreditNoteDetailSchema.parse(data);
};

export const markCreditNoteUsed = async (
  id: string,
  body: MarkCreditNoteUsedRequest,
): Promise<CreditNoteDetail> => {
  const { data } = await apiClient.post<unknown>(`/credit-notes/${id}/mark-used`, body);
  return CreditNoteDetailSchema.parse(data);
};

export const unmarkCreditNoteUsed = async (id: string): Promise<CreditNoteDetail> => {
  const { data } = await apiClient.post<unknown>(`/credit-notes/${id}/unmark-used`, {});
  return CreditNoteDetailSchema.parse(data);
};

// ----- v2: supplier AP settlement -----

export const recordSupplierPayment = async (
  invoiceSupplierId: string,
  body: RecordSupplierPaymentRequest,
): Promise<RecordSupplierPaymentResponse> => {
  const { data } = await apiClient.post<unknown>(
    `/supplier-lines/${invoiceSupplierId}/payments`,
    body,
  );
  return RecordSupplierPaymentResponseSchema.parse(data);
};

export const getInvoiceSupplierLedger = async (invoiceId: string): Promise<SupplierLine[]> => {
  const { data } = await apiClient.get<unknown>(`/invoices/${invoiceId}/supplier-ledger`);
  return SupplierLineSchema.array().parse(data);
};

export const getSupplierLedger = async (
  supplierId: string,
  params: { from?: string; to?: string } = {},
): Promise<SupplierLedger> => {
  const { data } = await apiClient.get<unknown>(`/suppliers/${supplierId}/ledger`, { params });
  return SupplierLedgerSchema.parse(data);
};

export const getSupplierOutstanding = async (
  params: { from?: string; to?: string; supplierId?: string } = {},
): Promise<SupplierOutstandingReport> => {
  const { data } = await apiClient.get<unknown>('/reports/supplier-outstanding', { params });
  return SupplierOutstandingReportSchema.parse(data);
};

/**
 * Supplier payments report (what we HAVE paid, legacy `supplierpaymentreport`)
 * - the settlement rows across invoices for a period plus the running total
 * paid + total TDS withheld. Complements supplier-outstanding (what is still
 * pending).
 */
export const getSupplierPayments = async (
  query: SupplierPaymentReportQuery,
): Promise<SupplierPaymentReport> => {
  const { data } = await apiClient.get<unknown>('/reports/supplier-payments', { params: query });
  return SupplierPaymentReportSchema.parse(data);
};

export const getSupplierPaymentStatus = async (
  query: SupplierPaymentStatusQuery,
): Promise<SupplierPaymentStatusReport> => {
  const { data } = await apiClient.get<unknown>('/reports/supplier-payment-status', {
    params: query,
  });
  return SupplierPaymentStatusReportSchema.parse(data);
};

export const getAgentReportCard = async (
  query: AgentReportCardQuery,
): Promise<AgentReportCardResponse> => {
  const { data } = await apiClient.get<unknown>('/reports/agent-report-card', { params: query });
  return AgentReportCardResponseSchema.parse(data);
};

// ----- Sales register (report #1) -----

export const getSalesRegister = async (
  query: SalesRegisterQuery,
): Promise<SalesRegisterResponse> => {
  const { data } = await apiClient.get<unknown>('/reports/sales-register', { params: query });
  return SalesRegisterResponseSchema.parse(data);
};

export const getClientOutstanding = async (
  query: ClientOutstandingQuery,
): Promise<ClientOutstandingResponse> => {
  const { data } = await apiClient.get<unknown>('/reports/client-outstanding', { params: query });
  return ClientOutstandingResponseSchema.parse(data);
};

export const getSupplierPaidOutstanding = async (
  query: SupplierPaidOutstandingQuery,
): Promise<SupplierPaidOutstandingResponse> => {
  const { data } = await apiClient.get<unknown>('/reports/supplier-paid-outstanding', {
    params: query,
  });
  return SupplierPaidOutstandingResponseSchema.parse(data);
};

export const getBalanceDueReport = async (
  query: BalanceDueReportQuery,
): Promise<BalanceDueReportResponse> => {
  const { data } = await apiClient.get<unknown>('/reports/balance-due', { params: query });
  return BalanceDueReportResponseSchema.parse(data);
};

export const getBankStatement = async (
  query: BankStatementQuery,
): Promise<BankStatementResponse> => {
  const { data } = await apiClient.get<unknown>('/reports/bank-statement', { params: query });
  return BankStatementResponseSchema.parse(data);
};

export const getOperationPending = async (
  query: OperationPendingQuery,
): Promise<OperationPendingResponse> => {
  const { data } = await apiClient.get<unknown>('/reports/operation-pending', { params: query });
  return OperationPendingResponseSchema.parse(data);
};

export const getDepositBooking = async (
  query: DepositBookingQuery,
): Promise<DepositBookingResponse> => {
  const { data } = await apiClient.get<unknown>('/reports/deposit-booking', { params: query });
  return DepositBookingResponseSchema.parse(data);
};

// ----- Bank book reconciliation -----

export const previewBankReconcile = async (
  body: BankReconcileUploadRequest,
): Promise<BankReconcilePreviewResponse> => {
  const { data } = await apiClient.post<unknown>('/reports/bank-reconcile/preview', body);
  return BankReconcilePreviewResponseSchema.parse(data);
};

export const commitBankReconcile = async (
  body: BankReconcileCommitRequest,
): Promise<BankReconcileCommitResult> => {
  const { data } = await apiClient.post<unknown>('/reports/bank-reconcile/commit', body);
  return BankReconcileCommitResultSchema.parse(data);
};

export const getBankReconcileHistory = async (): Promise<BankReconcileRunList> => {
  const { data } = await apiClient.get<unknown>('/reports/bank-reconcile/history');
  return BankReconcileRunListSchema.parse(data);
};

export const getBankReconcileRunDetail = async (id: string): Promise<BankReconcileRunDetail> => {
  const { data } = await apiClient.get<unknown>(`/reports/bank-reconcile/history/${id}`);
  return BankReconcileRunDetailSchema.parse(data);
};

/**
 * Replace the whole installment PLAN (amount + due date terms) for one supplier
 * payable line. Working-capital scheduling only - it never touches settlement.
 */
export const saveSupplierPaymentPlan = async (
  invoiceSupplierId: string,
  body: SaveSupplierPaymentPlanRequest,
): Promise<void> => {
  await apiClient.put<unknown>(`/supplier-lines/${invoiceSupplierId}/payment-plan`, body);
};

/** Working-capital schedule: planned supplier installments aged into buckets. */
export const getSupplierPaymentSchedule = async (
  query: SupplierPaymentScheduleQuery = {},
): Promise<SupplierPaymentSchedule> => {
  const { data } = await apiClient.get<unknown>('/reports/supplier-payment-schedule', {
    params: query,
  });
  return SupplierPaymentScheduleSchema.parse(data);
};

// ----- v2: GST output register -----

export const getGstOutputRegister = async (
  query: GstOutputRegisterQuery,
): Promise<GstOutputRegister> => {
  const { data } = await apiClient.get<unknown>('/reports/gst-output-register', { params: query });
  return GstOutputRegisterSchema.parse(data);
};

export const getTcsRegister = async (query: TcsRegisterQuery): Promise<TcsRegister> => {
  const { data } = await apiClient.get<unknown>('/reports/tcs-register', { params: query });
  return TcsRegisterSchema.parse(data);
};

// ----- Multi-bank + void (edit/delete payments) -----

export const getBankAccountOptions = async (): Promise<BankAccount[]> => {
  const { data } = await apiClient.get<unknown>('/bank-accounts/options');
  return BankAccountSchema.array().parse(data);
};

export const listBankAccounts = async (): Promise<BankAccount[]> => {
  const { data } = await apiClient.get<unknown>('/settings/bank-accounts');
  return BankAccountSchema.array().parse(data);
};

export const createBankAccount = async (body: CreateBankAccount): Promise<BankAccount> => {
  const { data } = await apiClient.post<unknown>('/settings/bank-accounts', body);
  return BankAccountSchema.parse(data);
};

export const updateBankAccount = async (
  id: string,
  body: UpdateBankAccount,
): Promise<BankAccount> => {
  const { data } = await apiClient.put<unknown>(`/settings/bank-accounts/${id}`, body);
  return BankAccountSchema.parse(data);
};

export const setDefaultBankAccount = async (id: string): Promise<BankAccount> => {
  const { data } = await apiClient.put<unknown>(`/settings/bank-accounts/${id}/default`, {});
  return BankAccountSchema.parse(data);
};

export const removeBankAccount = async (id: string): Promise<void> => {
  await apiClient.delete<unknown>(`/settings/bank-accounts/${id}`);
};

/** Void a customer receipt (edit = void + re-record; delete = void). */
export const voidReceipt = async (
  invoiceId: string,
  receiptId: string,
): Promise<RecordReceiptResponse> => {
  const { data } = await apiClient.post<unknown>(
    `/invoices/${invoiceId}/receipts/${receiptId}/void`,
    {},
  );
  return RecordReceiptResponseSchema.parse(data);
};

/** Void a supplier payment (edit = void + re-record; delete = void). */
export const voidSupplierPayment = async (
  paymentId: string,
): Promise<RecordSupplierPaymentResponse> => {
  const { data } = await apiClient.post<unknown>(`/supplier-payments/${paymentId}/void`, {});
  return RecordSupplierPaymentResponseSchema.parse(data);
};

// ===== Supplier-change approvals (ADMIN) =====

/**
 * Row of the supplier-change approval queue. No shared schema exists; this
 * mirrors the raw InvoiceSupplierChangeApproval row the controller returns
 * (BigInt paise serialise as strings, dates as ISO strings).
 */
export const SupplierChangeApprovalRowSchema = z.object({
  id: z.string(),
  invoiceId: z.string(),
  invoiceSupplierId: z.string(),
  oldSupplierId: z.string().nullable(),
  newSupplierId: z.string().nullable(),
  reason: SupplierChangeReasonSchema,
  settledSnapshotPaise: z.coerce.string(),
  payableSnapshotPaise: z.coerce.string(),
  requestGroupId: z.string(),
  status: z.enum(['PENDING', 'APPROVED', 'REJECTED', 'SUPERSEDED']),
  requestedById: z.string(),
  requestRemark: z.string().nullable(),
  reviewedById: z.string().nullable(),
  reviewedAt: z.string().nullable(),
  reviewRemark: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type SupplierChangeApprovalRow = z.infer<typeof SupplierChangeApprovalRowSchema>;

export interface SupplierChangeApprovalListQuery {
  status?: SupplierChangeApprovalRow['status'];
  invoiceId?: string;
  requestGroupId?: string;
}

export const listSupplierChangeApprovals = async (
  query: SupplierChangeApprovalListQuery = {},
): Promise<SupplierChangeApprovalRow[]> => {
  const { data } = await apiClient.get<unknown>('/invoice-supplier-change-approvals', {
    params: query,
  });
  return z.array(SupplierChangeApprovalRowSchema).parse(data);
};

export const supplierChangeApprovalsPendingCount = async (): Promise<{ count: number }> => {
  const { data } = await apiClient.get<unknown>('/invoice-supplier-change-approvals/pending-count');
  return z.object({ count: z.number().int() }).parse(data);
};

export const approveSupplierChange = async (id: string): Promise<SupplierChangeApprovalRow> => {
  const { data } = await apiClient.post<unknown>(
    `/invoice-supplier-change-approvals/${id}/approve`,
    {},
  );
  return SupplierChangeApprovalRowSchema.parse(data);
};

/** Remark REQUIRED. */
export const rejectSupplierChange = async (
  id: string,
  payload: { remark: string },
): Promise<SupplierChangeApprovalRow> => {
  const { data } = await apiClient.post<unknown>(
    `/invoice-supplier-change-approvals/${id}/reject`,
    payload,
  );
  return SupplierChangeApprovalRowSchema.parse(data);
};

export const bulkApproveSupplierChange = async (
  requestGroupId: string,
): Promise<{ approved: number }> => {
  const { data } = await apiClient.post<unknown>(
    '/invoice-supplier-change-approvals/bulk-approve',
    {
      requestGroupId,
    },
  );
  return z.object({ approved: z.number().int() }).parse(data);
};

// ===== Supplier refunds (ADMIN + FINANCE) =====

export const listSupplierRefunds = async (
  query: SupplierRefundListQuery = {},
): Promise<SupplierRefundRow[]> => {
  const { data } = await apiClient.get<unknown>('/supplier-refunds', { params: query });
  return z.array(SupplierRefundRowSchema).parse(data);
};

export const receiveSupplierRefund = async (
  id: string,
  payload: ReceiveSupplierRefundRequest,
): Promise<SupplierRefundRow> => {
  const { data } = await apiClient.post<unknown>(`/supplier-refunds/${id}/receive`, payload);
  return SupplierRefundRowSchema.parse(data);
};
